import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { platformApi } from '../api'
import type { Form, InboxItem, Submission } from '../types'

/** null = still loading. Secondary lists fall back to [] on error (they are optional). */
export interface HomeData {
    forms: Form[] | null
    formsError: string | null
    drafts: Submission[] | null
    recent: Submission[] | null
    inbox: InboxItem[] | null
    reloadForms: () => void
}

export function useHomeData(): HomeData {
    const [forms, setForms] = useState<Form[] | null>(null)
    const [formsError, setFormsError] = useState<string | null>(null)
    const [drafts, setDrafts] = useState<Submission[] | null>(null)
    const [recent, setRecent] = useState<Submission[] | null>(null)
    const [inbox, setInbox] = useState<InboxItem[] | null>(null)
    const [formsNonce, setFormsNonce] = useState(0)

    useEffect(() => {
        let live = true
        setForms(null)
        setFormsError(null)
        platformApi.listForms()
            .then((f) => live && setForms(f))
            .catch((e: unknown) => {
                if (!live) return
                setFormsError(e instanceof Error ? e.message : 'Could not load forms')
                setForms([])
            })
        return () => { live = false }
    }, [formsNonce])

    useEffect(() => {
        let live = true
        // Requests sent back to the user need their action, so they lead the drafts block.
        Promise.all([
            platformApi.listSubmissions({ scope: 'mine', status: 'returned', pageSize: 5 }),
            platformApi.listSubmissions({ scope: 'mine', status: 'draft', pageSize: 5 }),
        ])
            .then(([back, drafts]) => live && setDrafts([...(back.items ?? []), ...(drafts.items ?? [])]))
            .catch(() => live && setDrafts([]))
        platformApi.listSubmissions({ scope: 'mine', pageSize: 10 })
            .then((r) => live && setRecent((r.items ?? []).filter((s) => s.status !== 'draft').slice(0, 4)))
            .catch(() => live && setRecent([]))
        platformApi.inbox()
            .then((i) => live && setInbox(i ?? []))
            .catch(() => live && setInbox([]))
        return () => { live = false }
    }, [])

    const reloadForms = useCallback(() => setFormsNonce((n) => n + 1), [])
    return { forms, formsError, drafts, recent, inbox, reloadForms }
}

/** Create a submission for a form and open it in the editor. */
export function useStartForm() {
    const navigate = useNavigate()
    const [starting, setStarting] = useState<string | null>(null)

    const start = useCallback(async (f: Form) => {
        if (starting) return
        setStarting(f.id)
        try {
            const s = await platformApi.createSubmission(f.slug)
            void navigate({ to: '/submissions/$id/edit', params: { id: s.id } })
        } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Could not start the form')
            setStarting(null)
        }
    }, [navigate, starting])

    return { start, starting }
}
